import React, { useState } from 'react';

/*
 * HomeV2 — the "profession-playbook" front door (the A/B "B").
 * Flow: two doors -> smart profession/URL input -> instant per-profession
 * "playbook" (where/when/top idea) -> a 90%-built post whose "finish & publish"
 * = signup. See the project_profession_playbook_funnel memory for the full
 * concept + the real architecture (cached profession_playbooks, signup
 * rearrange — both on the auth/backend track).
 *
 * THIS IS A CLICKABLE PROTOTYPE on SAMPLE DATA. Mounted at /v2 (App.js); the
 * current home page stays at / untouched. The playbook content below is a
 * stand-in for the future server-generated, per-profession cache.
 */

const BRAND = '#00aef7';

const PLAYBOOKS = {
  plumber: {
    label: 'plumbers',
    platforms: ['Facebook', 'Instagram', 'YouTube'],
    bestTime: 'Tue–Thu, 7–9am',
    topIdea: 'Before / after fixes',
    stat: { text: '“plumber near me” searches have roughly tripled in 5 years', source: 'Google Trends' },
    post: {
      headline: '5 signs your water heater is about to fail',
      caption: "Catch it early and it's a $200 fix. Catch it late and you're mopping the garage. Here's what to watch for at home...",
      imageLabel: 'your before / after photo',
    },
  },
  electrician: {
    label: 'electricians',
    platforms: ['Facebook', 'YouTube', 'Instagram'],
    bestTime: 'Mon–Wed, 6–8am',
    topIdea: 'Safety-check tips',
    stat: { text: 'home electrical safety is a top local-search category', source: 'industry data' },
    post: {
      headline: '3 outlets in your home you should never ignore',
      caption: "Warm, buzzing, or browning? Stop. Here's what each warning sign actually means before it turns into a fire...",
      imageLabel: 'your panel / job photo',
    },
  },
  realtor: {
    label: 'real estate agents',
    platforms: ['Instagram', 'Facebook', 'YouTube'],
    bestTime: 'Thu–Sat, 11am–1pm',
    topIdea: 'Just-listed walkthroughs',
    stat: { text: 'most buyers begin their home search on social and video', source: 'NAR' },
    post: {
      headline: '3 things buyers miss on the first walkthrough',
      caption: "The kitchen sells the house — but these three things decide the offer. Save this before your next showing...",
      imageLabel: 'your listing photo',
    },
  },
  hvac: {
    label: 'HVAC pros',
    platforms: ['Facebook', 'YouTube', 'Instagram'],
    bestTime: 'Mon–Wed, 7–9am',
    topIdea: 'Seasonal tune-up tips',
    stat: { text: 'HVAC demand is highly seasonal and intensely local', source: 'industry data' },
    post: {
      headline: 'Why your energy bill jumped this month',
      caption: "It's usually one of three things — and two you can fix this weekend. Here's how to tell which one it is...",
      imageLabel: 'your install / unit photo',
    },
  },
};

const FALLBACK = {
  label: 'local businesses like yours',
  platforms: ['Facebook', 'Instagram', 'YouTube'],
  bestTime: 'Tue–Thu, 7–9am',
  topIdea: 'Behind-the-scenes',
  stat: { text: 'most customers check social before they call a local business', source: 'BrightLocal' },
  post: {
    headline: 'The one question every customer asks us',
    caption: "We hear it every week — so here's the honest answer, the part nobody tells you, and what to do about it...",
    imageLabel: 'your photo here',
  },
};

/* Prototype stand-in for canonical-profession normalization. The real thing
   routes free text into the existing profession taxonomy and only mints a new
   bucket when genuinely new. */
function normalize(text) {
  const t = text.toLowerCase();
  if (/plumb|drain|pipe/.test(t)) return 'plumber';
  if (/electric|wiring/.test(t)) return 'electrician';
  if (/real ?estate|realtor|agent|broker/.test(t)) return 'realtor';
  if (/hvac|heating|cooling|air ?condition|furnace/.test(t)) return 'hvac';
  return null;
}

const DOORS = [
  { id: 'new', title: 'My business is new to social media', sub: 'I barely post — show me how' },
  { id: 'ideas', title: "I post, but I'm out of ideas", sub: 'give me ideas that actually work' },
];

const QUICK = [
  { key: 'plumber', label: 'Plumber' },
  { key: 'electrician', label: 'Electrician' },
  { key: 'realtor', label: 'Real estate agent' },
  { key: 'hvac', label: 'HVAC' },
];

function DoorIcon({ id }) {
  if (id === 'new') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
        <path d="M12 22V11" />
        <path d="M12 12C12 8 9 6 4 6c0 5 3 7 8 6Z" />
        <path d="M12 13c0-3 3-5 8-5 0 4-3 6-8 5Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a6 6 0 0 0-4 10.5c1 1 1.5 2 1.5 3.5h5c0-1.5.5-2.5 1.5-3.5A6 6 0 0 0 12 2Z" />
    </svg>
  );
}

function Fact({ label, children }) {
  return (
    <div className="rounded-xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3 text-left">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">{label}</div>
      <div className="text-sm font-medium text-gray-900">{children}</div>
    </div>
  );
}

export default function HomeV2() {
  const [step, setStep] = useState('entry'); // entry | input | playbook
  const [door, setDoor] = useState(null);
  const [text, setText] = useState('');

  const profKey = normalize(text);
  const pb = profKey ? PLAYBOOKS[profKey] : FALLBACK;

  const reset = () => { setStep('entry'); setDoor(null); setText(''); };
  const pickDoor = (id) => { setDoor(id); setStep('input'); };
  const submit = (presetKey) => {
    if (presetKey) setText(QUICK.find((q) => q.key === presetKey)?.label || presetKey);
    setStep('playbook');
  };

  return (
    <main className="flex-grow bg-white">
      <div className="mx-auto w-[92%] max-w-3xl min-h-[78vh] flex flex-col justify-center py-12 md:py-16">

        {step === 'entry' && (
          <div className="text-center">
            <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-gray-400 mb-3">for local, small businesses</div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.1]">
              Social media that brings in <span style={{ color: BRAND }}>customers</span> — without the work.
            </h1>
            <p className="mt-5 text-gray-500 text-lg">Which one sounds like you?</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4 text-left">
              {DOORS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => pickDoor(d.id)}
                  className="group rounded-2xl ring-1 ring-gray-200 hover:ring-2 hover:ring-[#00aef7] p-6 transition-all hover:shadow-lg bg-white"
                >
                  <span style={{ color: BRAND }}><DoorIcon id={d.id} /></span>
                  <div className="mt-3 text-lg font-bold text-gray-900 leading-snug">{d.title}</div>
                  <div className="mt-1 text-sm text-gray-500 flex items-center gap-1">
                    {d.sub} <span className="transition-transform group-hover:translate-x-1">→</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-7 text-sm text-gray-400">
              just want to look around? <span style={{ color: BRAND }} className="font-semibold cursor-pointer">see how it works</span>
            </div>
            <div className="mt-2 text-xs text-gray-400">plumbers · salons · clinics · realtors · trainers · piano teachers</div>
          </div>
        )}

        {step === 'input' && (
          <div className="text-center">
            <button type="button" onClick={reset} className="text-sm text-gray-400 hover:text-gray-600 mb-6">← back</button>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">What do you do?</h1>
            <p className="mt-3 text-gray-500">We'll show you exactly where, when, and what to post — free.</p>
            <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="mt-7">
              <input
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. plumber — or paste your website"
                className="w-full rounded-full ring-1 ring-gray-300 focus:ring-2 focus:ring-[#00aef7] px-6 py-4 text-lg outline-none"
              />
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {QUICK.map((q) => (
                  <button
                    key={q.key}
                    type="button"
                    onClick={() => submit(q.key)}
                    className="rounded-full ring-1 ring-gray-200 hover:ring-[#00aef7] px-4 py-1.5 text-sm text-gray-700 hover:text-[#00aef7] transition-colors"
                  >
                    {q.label}
                  </button>
                ))}
              </div>
              <button
                type="submit"
                className="mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-white text-base font-semibold transition-transform hover:scale-[1.02]"
                style={{ background: BRAND }}
              >
                Show me my playbook →
              </button>
            </form>
            <div className="mt-3 text-xs text-gray-400">paste your site and our AI tailors this to your actual business</div>
          </div>
        )}

        {step === 'playbook' && (
          <div>
            <button type="button" onClick={reset} className="text-sm text-gray-400 hover:text-gray-600 mb-5">← start over</button>
            <div className="text-xs text-gray-400 mb-2">
              {door === 'ideas' ? 'I post, but I need ideas' : 'new to social media'} <span className="mx-1">›</span> {pb.label}
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900 leading-tight">
              {door === 'ideas'
                ? <>Fresh ideas for {pb.label} — and where they land best.</>
                : <>Here's where, when &amp; what {pb.label} should post.</>}
            </h2>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Fact label="post where">{pb.platforms.join(', ')}</Fact>
              <Fact label="best time">{pb.bestTime}</Fact>
              <Fact label="top idea">{pb.topIdea}</Fact>
            </div>
            <p className="mt-3 text-sm text-gray-500 italic">
              {pb.stat.text} <span className="text-gray-400 not-italic">({pb.stat.source})</span>
            </p>

            <div className="mt-8 text-sm text-gray-500">and we already started your first one —</div>
            <div className="mt-2 rounded-2xl ring-1 ring-gray-200 overflow-hidden shadow-sm">
              <div className="h-40 bg-gray-50 flex items-center justify-center text-gray-400 text-sm">{pb.post.imageLabel}</div>
              <div className="p-5">
                <span className="inline-block text-[11px] font-semibold rounded-full px-2.5 py-0.5 ring-1" style={{ color: BRAND, borderColor: 'rgba(0,174,247,0.4)' }}>
                  draft · 90% done
                </span>
                <h3 className="mt-2 text-lg font-bold text-gray-900">{pb.post.headline}</h3>
                <p className="mt-1.5 text-gray-600 leading-relaxed">{pb.post.caption}</p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <a
                href="/?modal=signup"
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-white text-lg font-semibold transition-transform hover:scale-[1.03] shadow-lg"
                style={{ background: BRAND }}
              >
                {door === 'ideas' ? 'Get fresh ideas + publish →' : 'Finish & publish this →'}
              </a>
              <div className="mt-3 text-sm text-gray-500">we design it, you hit publish — about 5 minutes</div>
              <div className="mt-4 text-xs text-gray-400">
                not ready? <span style={{ color: BRAND }} className="font-semibold cursor-pointer">email me my playbook</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
